import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Fade,
  Container,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/authService';

const LoginPage = () => {
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    
    setError('');
    
    // Input validation
    if (!email.trim() || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.requiresPasswordChange) {
      localStorage.setItem('openSecuritySettings', 'true');
      navigate('/settings'); 
    } else {
        if (result.success){
          const userRole = result.user?.role;
          if (userRole === 'SUPERADMIN') {
            navigate('/super-admin/dashboard');
          }else{
            navigate('/dashboard');
          }
        }
        
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !isLoading) {
      handleLogin(event);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `
          radial-gradient(circle at 25% 25%, rgba(168, 133, 29, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(168, 133, 29, 0.03) 0%, transparent 50%)
        `,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              backgroundColor: 'rgba(26, 26, 26, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(168, 133, 29, 0.3)',
              borderRadius: 2,
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(168, 133, 29, 0.1)
              `,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60%',
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(168, 133, 29, 0.6), transparent)',
              },
            }}
          >
            {/* Logo Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src="./Logo-preview.png"
                alt="Alfresko AV"
                style={{
                  height: '64px',
                  marginBottom: '16px',
                  filter: 'drop-shadow(0 2px 8px rgba(168, 133, 29, 0.3))',
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: '#ffffff',
                  fontWeight: 300,
                  letterSpacing: '0.02em',
                  mb: 1,
                }}
              >
                Administrative Access
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                }}
              >
                Secure authentication required
              </Typography>
            </Box>

            {/* Login Form */}
            <Box
              component="form"
              onSubmit={handleLogin}
              onKeyPress={handleKeyPress}
              sx={{ width: '100%' }}
            >
              <TextField
                fullWidth
                variant="standard"
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.9rem',
                    '&.Mui-focused': {
                      color: '#a8851d',
                    },
                  },
                  '& .MuiInput-root': {
                    color: '#ffffff',
                    fontSize: '1rem',
                    '&:before': {
                      borderBottomColor: 'rgba(255, 255, 255, 0.3)',
                      transition: 'border-bottom-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    '&:hover:before': {
                      borderBottomColor: 'rgba(168, 133, 29, 0.6)',
                    },
                    '&:after': {
                      borderBottomColor: '#a8851d',
                      borderBottomWidth: '2px',
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                variant="standard"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                sx={{
                  mb: 4,
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.9rem',
                    '&.Mui-focused': {
                      color: '#a8851d',
                    },
                  },
                  '& .MuiInput-root': {
                    color: '#ffffff',
                    fontSize: '1rem',
                    '&:before': {
                      borderBottomColor: 'rgba(255, 255, 255, 0.3)',
                      transition: 'border-bottom-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    '&:hover:before': {
                      borderBottomColor: 'rgba(168, 133, 29, 0.6)',
                    },
                    '&:after': {
                      borderBottomColor: '#a8851d',
                      borderBottomWidth: '2px',
                    },
                  },
                }}
              />

              {/* Error Message */}
              {error && (
                <Fade in timeout={300}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      mb: 3,
                      textAlign: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                    }}
                  >
                    {error}
                  </Typography>
                </Fade>
              )}

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="outlined"
                disabled={isLoading}
                sx={{
                  height: 56,
                  borderRadius: 1,
                  borderColor: '#a8851d',
                  color: '#a8851d',
                  fontSize: '1rem',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  textTransform: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: '#a8851d',
                    backgroundColor: '#a8851d',
                    color: '#1a1a1a',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(168, 133, 29, 0.3)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(168, 133, 29, 0.3)',
                    color: 'rgba(168, 133, 29, 0.3)',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {isLoading ? (
                  <Fade in timeout={200}>
                    <CircularProgress
                      size={24}
                      sx={{
                        color: '#a8851d',
                      }}
                    />
                  </Fade>
                ) : (
                  <Fade in timeout={200}>
                    <span>Access Portal</span>
                  </Fade>
                )}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Link 
                component="button"
                variant="body2"
                onClick={() => navigate('/forgot-password')}
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  '&:hover': { color: '#a8851d' }
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                }}
              >
                Powered by Alfresko AV
              </Typography>

              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.3)', 
                  fontSize: '0.7rem',
                  display: 'block', 
                }}
              >
                v2.1.6
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;